<script setup lang="ts">
import _400FImage from './400f.svg';
import AuthorityHeroImage from './authority.svg';
import DirectLandingFromAdHeroImage from './directLandingFromAdHero.svg';
import hokennomadoguchiTsuhanImage from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/RegisterUserTemplate/hokennomadoguchi_tsuhan.png/hokennomadoguchi_tsuhan.webp';
import InsuranceProductConsultationRequestHero from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/RegisterUserTemplate/insuranceProductConsultationRequestHero.jpg/insuranceProductConsultationRequestHero.webp';
import InsuranceProductDocumentRequestHero from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/RegisterUserTemplate/insuranceProductDocumentRequestHero.jpg/insuranceProductDocumentRequestHero.webp';
import KiraboshiHeroImage from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/RegisterUserTemplate/kiraboshiHero.png/kiraboshiHero.webp';
import LpOkaneKenkoHero from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/RegisterUserTemplate/lpOkaneKenkoHero.png/lpOkaneKenkoHero.webp';
import MoneyForwardHeroImage from './moneyForwardRegisterHero.svg';
import NewDiagnosisHeroImage from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/RegisterUserTemplate/newDiagnosisHero.png/newDiagnosisHero.webp';
import PontaHeroImage from './pontaRegisterHero.svg';
import RakutenInhouseAdvisorHeroImage from './rakutenInhouseAdvisorRegisterHero.svg';
import DefaultHeroImage from './registerHero.svg';
import SmccHeroImage from './smccRegisterHero.svg';
import { registerVariants, useRegisterValiant } from './useRegisterValiant';
import ZjkHeroImage from './zjkRegisterHero.svg';
import { useGetAuthUrl } from '@/composables/microservice/lexington/useGetAuthUrl';
import LoginLink from 'theme/components/atoms/LoginLink.vue';
import NuxtLinkFlat from 'theme/components/atoms/NuxtLinkFlat.vue';
import ButtonLineLoginVariant from 'theme/components/molecules/ButtonLineLoginVariant.vue';
import RecommendationPopup from 'theme/components/molecules/RecommendationPopup.vue';
import { getStringQuery } from 'theme/utils/getQuery';
import type { ChannelId } from '~/entity/channel';
import { toChannel } from '~/entity/channel';
import { Channel } from '~/gql/graphql';

const route = useRoute();

const registerEmail = computed(() => ({
  path: '/register/email',
  query: route.query,
}));

const channelId = useClientSideOnlyComputed(
  getIntQuery(route.query.inflow_channel) as ChannelId,
  () => {
    const { channelId } = useSessionChannel();
    return channelId;
  }
);

const channel = computed(() => toChannel(channelId.value));

const financialServicesAgent = useFinancialServicesAgent();
const agent = financialServicesAgent.getFromQueryParam();
onMounted(() => {
  if (agent) {
    financialServicesAgent.storeBrowserStorage(agent);
  }
});

const { registerVariant } = useRegisterValiant();

const isDirectLandingFromAd = computed(
  () => registerVariant === registerVariants.directLandingFromAd
);

const heroImage = ref<string | undefined>(undefined);

onMounted(() => {
  if (channel.value === Channel.Ponta) {
    heroImage.value = PontaHeroImage;
    return;
  }

  switch (registerVariant) {
    case registerVariants.directLandingFromAd:
      heroImage.value = DirectLandingFromAdHeroImage;
      return;
    case registerVariants.insuranceProductDocumentRequest:
      heroImage.value = InsuranceProductDocumentRequestHero;
      return;
    case registerVariants.insuranceProductConsultationRequest:
      heroImage.value = InsuranceProductConsultationRequestHero;
      return;
    case registerVariants.lpOkaneKenkoLifeplanning:
      heroImage.value = LpOkaneKenkoHero;
      return;
    // case registerVariants.lp400F2:
    //   heroImage.value = DefaultHeroImage;
    //   return;
    case registerVariants.lpKiraboshi:
      heroImage.value = KiraboshiHeroImage;
      return;
    case registerVariants.lpRakutenInhouseAdvisor:
      heroImage.value = RakutenInhouseAdvisorHeroImage;
      return;
    case registerVariants.lpSmcc:
      heroImage.value = SmccHeroImage;
      return;
    case registerVariants.lp400F:
      heroImage.value = _400FImage;
      return;
    case registerVariants.lpZjk:
      heroImage.value = ZjkHeroImage;
      return;
    case registerVariants.lpHokennomadoguchiTsuhan:
      heroImage.value = hokennomadoguchiTsuhanImage;
      return;
    case registerVariants.lpMoneyForward:
      heroImage.value = MoneyForwardHeroImage;
      return;
    // default:
    //   heroImage.value = DefaultHeroImage;
    //   return;
  }

  // NOTE: 新診断結果画面が全チャネルに展開された後に、この画像はdefaultとする
  if (
    [
      Channel.Okaneko,
      Channel.Rakuten,
      Channel.Yahoo,
      Channel.Sbi,
      Channel.FourHundredF,
    ].includes(channel.value as Channel)
  ) {
    heroImage.value = NewDiagnosisHeroImage;
    return;
  }

  heroImage.value = DefaultHeroImage;
});

const onClickLineLogin = async () => {
  const redirect = getStringQuery(route.query.redirect);
  const session = useSession();

  const searchParams = {
    redirect: redirect ?? '/my',
    channelId: channelId.value,
    shortSessionId: session.shortSessionId.value,
    longSessionId: session.longSessionId.value,
  };

  const { fetch } = useGetAuthUrl();
  try {
    window.location.href = await fetch(searchParams);
  } catch (_) {
    // TODO: エラーハンドリングする
  }
};
</script>

<template>
  <main>
    <div
      :class="[
        $style.registration,
        $style[`registration--${channel}`],
        $style[`registration--${registerVariant}`],
        { [$style['registration--directLanding']]: isDirectLandingFromAd },
      ]"
    >
      <img
        v-if="heroImage"
        width="375"
        height="210"
        :src="heroImage"
        alt="会員登録ページ"
        :class="$style.registration__image"
      />
    </div>
    <div :class="$style.container">
      <img
        v-if="
          isDirectLandingFromAd ||
          registerVariant === registerVariants.lpOkaneKenkoLifeplanning
        "
        :class="[$style.container__image, $style.image]"
        :src="AuthorityHeroImage"
        alt=""
      />
      <div :class="[$style.container__content, $style.content]">
        <p
          v-if="channel === Channel.RakutenInHouseAdvisor"
          :class="[
            $style['content__message'],
            $style.message,
            $style['message--RAKUTEN_IN_HOUSE_ADVISOR'],
          ]"
        >
          LINE
          <span :class="$style['message--RAKUTEN_IN_HOUSE_ADVISOR__asterisk']">
            （※）
          </span>
          もしくはメールアドレスで
          <br />
          利用登録をして始めましょう
          <br />
          <span :class="$style['message--RAKUTEN_IN_HOUSE_ADVISOR__note']">
            ※楽天証券LINE公式アカウントとは異なります
          </span>
        </p>
        <p
          v-else-if="
            registerVariant === registerVariants.insuranceProductDocumentRequest
          "
          :class="[$style.content__message, $style.message]"
        >
          住所入力不要、チャットですぐに<br />
          資料が受け取れます
        </p>
        <p
          v-else-if="
            registerVariant ===
            registerVariants.insuranceProductConsultationRequest
          "
          :class="[$style.content__message, $style.message]"
        >
          カンタン会員登録ですぐに専門家へ<br />
          チャットで相談できます
        </p>
        <p
          v-else-if="
            channel === Channel.ZaikeiJutakuKinyu ||
            channel === Channel.HokenNoMadoguchi ||
            channel === Channel.Kiraboshi
          "
          :class="[$style['content__message'], $style.message]"
        >
          LINEもしくはメールアドレスで<br />
          会員登録をして始めましょう
        </p>
        <p
          v-else-if="registerVariant === registerVariants.lpMoneyForward"
          :class="[$style['content__message'], $style.message]"
        >
          診断をはじめる方法を選ぶ！
        </p>
        <p
          v-else-if="
            registerVariant === registerVariants.lpOkaneKenkoLifeplanning
          "
          :class="[$style['content__message'], $style.message]"
        >
          はじめる方法を選んでにゃ
        </p>
        <p v-else :class="[$style['content__message'], $style.message]">
          診断をはじめる方法を選んでにゃ！
        </p>
        <div :class="[$style.content__line, $style.line]">
          <RecommendationPopup :class="$style.line__popup" />
          <ButtonLineLoginVariant
            :class="$style.line__button"
            @on-click-line-login="onClickLineLogin"
          />
        </div>
        <NuxtLinkFlat
          :class="$style.content__email"
          variant="primary"
          :to="registerEmail"
        >
          メールアドレスではじめる
        </NuxtLinkFlat>
        <div :class="[$style.content__policies, $style.policies]">
          ※<NuxtLink
            :to="{ path: '/policies/tos' }"
            target="_blank"
            :class="$style.policies__link"
            >利用規約</NuxtLink
          >
          と
          <NuxtLink
            :to="{ path: '/policies/privacy' }"
            target="_blank"
            :class="$style.policies__link"
          >
            プライバシーポリシー
          </NuxtLink>
          をお読みいただき、同意の上で開始してください。
        </div>
        <LoginLink :class="$style.content__login"
          >会員登録済みの方はこちら</LoginLink
        >
        <div
          v-if="channel === Channel.RakutenInHouseAdvisor"
          :class="[$style.content__notice, $style.notice]"
        >
          <p :class="$style.notice__title">ご注意</p>
          <p :class="[$style.notice__text, $style.text]">
            <em :class="$style.text__strong"
              >当画面以降「オカネコ」の利用登録画面に遷移します。</em
            >
            「オカネコ」のチャットシステムを利用するための登録となりますが、ご相談は楽天証券がお答えします。<br />
            <span :class="$style['notice__text--caution']">
              回答に個人の特定が必要なお問い合わせはお答えできません。ご利用時はチャット本文に個人情報を入力しないようお願いします。万が一お客さまが個人情報を入力された場合について、楽天証券は一切の責任を負いません。
            </span>
          </p>
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" module>
$content-max-width: 375px;
$hero-image-max-width: 480px;

.registration {
  background-color: $color-surface-accent-primary-light;
  display: flex;
  justify-content: center;

  &__image {
    width: 100%;
    max-width: $hero-image-max-width;
  }
}

.registration--RAKUTEN_IN_HOUSE_ADVISOR {
  background: linear-gradient(
    170deg,
    #fee7e8 0%,
    #fee7e8 50%,
    #fec8cb 50%,
    #fec8cb 100%
  );
}

.registration--lp_okane_kenko_jp__conc_lifeplan {
  background-color: $color-okaneko-blue;
}

.registration--directLanding {
  background-color: $color-okaneko-blue;
}

.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &__content {
    padding: 24px;
    max-width: $content-max-width;
    display: flex;
    justify-content: center;
    flex-direction: column;
  }
}

.image {
  margin: 12px 0;
  padding: 0 24px;
}

.content {
  &__line {
    text-align: center;
  }

  &__email {
    width: 100%;
    font-size: 16px;
    height: 48px;
    margin: 16px 0;
  }

  &__policies {
    font-size: 12px;
  }

  &__login {
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: bold;
    margin-top: 24px;
  }

  &__error {
    color: $color-caution-2;
  }
}

.message {
  color: $color-text-and-object-high-emphasis;
  font-size: 18px;
  font-weight: bold;
  text-align: center;

  &--RAKUTEN_IN_HOUSE_ADVISOR {
    font-size: 16px;

    &__asterisk {
      font-size: 10px;
    }

    &__note {
      font-size: 10px;
      font-weight: normal;
    }
  }
}

.line {
  @include create-stacking-context;

  &__popup {
    position: relative;
    transform: translateY(16px);
    display: inline-flex;
    justify-content: center;
    z-index: 1;
  }

  &__button {
    width: 100%;
  }
}

.policies {
  &__link {
    color: $color-text-and-object-accent-primary;
  }
}

.notice {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  border-radius: 8px;
  background-color: #fff7e2;
  padding: 16px;

  &__title {
    text-align: center;
    font-size: 14px;
    font-weight: 600;
  }

  &__text--caution {
    color: $color-caution-2;
    font-weight: bold;
  }
}

.text {
  font-size: 12px;

  &__strong {
    font-weight: 600;
  }
}
</style>
